import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateOrganizationModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [primaryContactEmail, setPrimaryContactEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [slugEdited, setSlugEdited] = useState(false);

  const mutation = useMutation({
    mutationFn: async () =>
      apiClient.post('/organizations', {
        name,
        slug,
        ...(primaryContactEmail ? { primaryContactEmail } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['organizations'] });
      setName('');
      setSlug('');
      setPrimaryContactEmail('');
      setSlugEdited(false);
      setError(null);
      onClose();
    },
    onError: (err) => {
      const data = (
        err as {
          response?: {
            data?: { message?: string; details?: { fieldErrors?: Record<string, string[]> } };
          };
        }
      ).response?.data;
      const fieldErrors = data?.details?.fieldErrors;
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        const formatted = Object.entries(fieldErrors)
          .map(([field, messages]) => `${field} : ${messages.join(', ')}`)
          .join(' • ');
        setError(formatted);
      } else {
        setError(data?.message ?? 'Échec de création');
      }
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  function slugify(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function handleNameChange(value: string) {
    setName(value);
    // Auto-régénère le slug tant que l'utilisateur ne l'a pas édité manuellement.
    if (!slugEdited) setSlug(slugify(value));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Créer une organisation cliente"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button form="create-org-form" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Création…' : 'Créer'}
          </Button>
        </>
      }
    >
      <form id="create-org-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </div>
        )}
        <div>
          <label htmlFor="org-name" className="mb-1 block text-xs font-medium text-slate-700">
            Nom de l'organisation
          </label>
          <input
            id="org-name"
            type="text"
            required
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            placeholder="Acme SAS"
          />
        </div>
        <div>
          <label htmlFor="org-slug" className="mb-1 block text-xs font-medium text-slate-700">
            Identifiant (slug)
          </label>
          <input
            id="org-slug"
            type="text"
            required
            minLength={2}
            pattern="[a-z0-9-]+"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugEdited(true);
            }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            placeholder="acme"
          />
          <p className="mt-1 text-[11px] text-slate-500">a-z, 0-9, tirets — utilisé en interne</p>
        </div>
        <div>
          <label htmlFor="org-contact" className="mb-1 block text-xs font-medium text-slate-700">
            E-mail de contact (optionnel)
          </label>
          <input
            id="org-contact"
            type="email"
            value={primaryContactEmail}
            onChange={(e) => setPrimaryContactEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            placeholder="contact@acme.test"
          />
        </div>
      </form>
    </Modal>
  );
}
